/**
 * Layout 7: Summit
 * Photo-first outdoor adventure layout - designed for when images are available
 * Uses placeholder gradients where photos would go
 *
 * Structure:
 * - Full-bleed hero (for landscape photo)
 * - Horizontal destination carousel (image cards)
 * - Dedicated map section
 * - Stats bar
 * - Place grid (image cards)
 * - CTA
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { NavigationControl } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import {
  type PublishedLandingPage,
  type PlaceFeature,
  type PlaceGeoJSON,
  PLACE_TYPE_META,
  PlaceType,
  getPlaceTrack,
} from "../../lib/api";
import { FIT_BOUNDS_PADDING, FLY_TO_OPTIONS, FLY_TO_ZOOM } from "../../lib/mapConstants";
import { routes } from "../../lib/routes";
import { useShare } from "../../hooks/useShare";
import { NomaMap, AdventureMapPlacesLayer, SelectedPlaceLayer, RouteTrackLayer } from "../../components/map";

interface SummitLandingProps {
  mapData: PublishedLandingPage;
  places: PlaceGeoJSON;
}

// Unsplash image URLs for placeholders
const photoUrls = {
  hero: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&h=1080&fit=crop&q=80",
  landscape: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop&q=80",
  restaurant: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop&q=80",
  cafe: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&h=600&fit=crop&q=80",
  grocery: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop&q=80",
  hiking: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop&q=80",
  accommodation: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop&q=80",
};

// Get placeholder image URL based on place type
const getPlaceholderUrl = (type: PlaceType): string => {
  const map: Partial<Record<PlaceType, string>> = {
    restaurant: photoUrls.restaurant,
    cafe: photoUrls.cafe,
    "grocery-store": photoUrls.grocery,
    hiking: photoUrls.hiking,
    accommodation: photoUrls.accommodation,
  };
  return map[type] ?? photoUrls.landscape;
};

export default function SummitLanding({ mapData, places }: SummitLandingProps) {
  const share = useShare();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);

  const placesByType = useMemo(() => {
    const grouped = new Map<PlaceType, PlaceFeature[]>();
    places.features.forEach((place) => {
      const type = place.properties.type;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(place);
    });
    return grouped;
  }, [places.features]);

  const handleMapLoad = useCallback((map: MapboxMap) => {
    mapRef.current = map;
  }, []);

  const handlePlaceSelect = useCallback((place: PlaceFeature) => {
    setSelectedPlace(place);
    mapRef.current?.flyTo({
      ...FLY_TO_OPTIONS,
      center: place.geometry.coordinates as [number, number],
      zoom: FLY_TO_ZOOM,
    });
    mapContainerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      handlePlaceSelect(feature);
    },
    [handlePlaceSelect],
  );

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (mapData.slug) {
        share({
          url: routes.to.landingPages.bySlug(mapData.slug),
          title: mapData.name,
        });
      }
    },
    [share, mapData.slug, mapData.name],
  );

  const colors = {
    forest: "#1b4332",
    forestLight: "#2d6a4f",
    amber: "#f59e0b",
    amberDark: "#d97706",
    cream: "#fefdf8",
    dark: "#111827",
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* =============================================
          HERO: Full-bleed photo with overlay
          ============================================= */}
      <header
        className="relative h-screen min-h-[700px] flex flex-col bg-cover bg-center"
        style={{ backgroundImage: `url(${photoUrls.hero})` }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Navigation */}
        <nav className="relative z-20 px-6 md:px-10 lg:px-16 py-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className="text-white font-semibold tracking-wide">Adventure Map</span>
          </div>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
            style={{ background: colors.amber, color: colors.dark }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
            Share
          </button>
        </nav>

        {/* Hero content - centered */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-6 md:px-10">
          <div className="text-center max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm text-white/90 font-medium">{places.features.length} places to discover</span>
            </div>

            {/* Main title */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-[0.95]">
              {mapData.name}
            </h1>

            {mapData.description && (
              <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
                {mapData.description}
              </p>
            )}

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => scrollTo("destinations")}
                className="flex items-center gap-3 px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105"
                style={{ background: colors.amber, color: colors.dark }}
              >
                Explore Destinations
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7 7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => scrollTo("map")}
                className="flex items-center gap-3 px-8 py-4 rounded-full text-lg font-semibold bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
              >
                View Map
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 pb-8 flex justify-center">
          <div className="flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="animate-bounce"
            >
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </div>
        </div>
      </header>

      {/* =============================================
          FEATURED DESTINATIONS: Horizontal carousel
          ============================================= */}
      <section id="destinations" className="py-16 md:py-24" style={{ background: colors.cream }}>
        <div className="px-6 md:px-10 lg:px-16 mb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 max-w-7xl mx-auto">
            <div>
              <span className="text-sm font-bold uppercase tracking-widest mb-2 block" style={{ color: colors.forest }}>
                Featured
              </span>
              <h2 className="text-4xl md:text-5xl font-bold" style={{ color: colors.dark }}>
                Top Destinations
              </h2>
            </div>
            <p className="text-gray-600 max-w-md">
              Hand-picked spots that define this adventure. Each one carefully selected.
            </p>
          </div>
        </div>

        {/* Horizontal scroll */}
        <div className="relative">
          <div className="flex gap-6 overflow-x-auto px-6 md:px-10 lg:px-16 pb-4 scrollbar-hide">
            {places.features.slice(0, 6).map((place, idx) => (
              <button
                key={place.id}
                onClick={() => handlePlaceSelect(place)}
                className="group flex-shrink-0 w-[280px] md:w-[340px] lg:w-[calc((100vw-8rem-4.5rem)/3.3)] text-left"
              >
                {/* Image area */}
                <div
                  className="relative h-[240px] md:h-[300px] rounded-2xl overflow-hidden mb-4 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${getPlaceholderUrl(place.properties.type)})`,
                  }}
                >
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />

                  {/* Category badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm">
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.forest }}>
                      {PLACE_TYPE_META[place.properties.type].label}
                    </span>
                  </div>

                  {/* Number badge */}
                  <div
                    className="absolute bottom-4 right-4 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold"
                    style={{ background: colors.amber, color: colors.dark }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </div>

                  {/* View prompt on hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="px-6 py-3 rounded-full bg-white font-semibold" style={{ color: colors.dark }}>
                      View on Map →
                    </span>
                  </div>
                </div>

                {/* Content */}
                <h3
                  className="text-xl md:text-2xl font-bold mb-2 group-hover:underline underline-offset-4"
                  style={{ color: colors.dark }}
                >
                  {place.properties.name}
                </h3>
                {place.properties.description && (
                  <p className="text-gray-600 line-clamp-2 leading-relaxed">{place.properties.description}</p>
                )}
              </button>
            ))}

            {/* See all card */}
            <div
              className="flex-shrink-0 w-[280px] snap-start rounded-2xl flex flex-col items-center justify-center text-center p-8"
              style={{ background: colors.forest }}
            >
              <div className="text-6xl font-bold text-white mb-2">{places.features.length}</div>
              <div className="text-white/70 mb-6">Places to explore</div>
              <button
                onClick={() => scrollTo("all-places")}
                className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{ background: colors.amber, color: colors.dark }}
              >
                See All →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* =============================================
          MAP SECTION
          ============================================= */}
      <section id="map" className="relative" style={{ background: colors.dark }}>
        <div className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 max-w-7xl mx-auto mb-8">
            <div>
              <span className="text-sm font-bold uppercase tracking-widest mb-2 block" style={{ color: colors.amber }}>
                Interactive
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white">Explore the Map</h2>
            </div>
            <p className="text-white/60 max-w-md">Click any pin to learn more about each destination.</p>
          </div>
        </div>

        <div className="relative px-6 md:px-10 lg:px-16 pb-16">
          <div className="max-w-7xl mx-auto">
            <div
              ref={mapContainerRef}
              className="h-[500px] md:h-[600px] w-full rounded-2xl overflow-hidden"
              style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)" }}
            >
              <NomaMap bounds={places} padding={FIT_BOUNDS_PADDING.desktop} onLoad={handleMapLoad}>
                <NavigationControl position="top-right" />
                <AdventureMapPlacesLayer places={places} onClick={handlePlaceClick} />
                <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
                <SelectedPlaceLayer place={selectedPlace} />
              </NomaMap>
            </div>

            {/* Selected place card */}
            {selectedPlace && (
              <div className="absolute bottom-20 left-10 md:left-20 right-10 md:right-auto md:w-[400px] z-10">
                <div className="rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 shadow-2xl">
                  {/* Image */}
                  <div
                    className="h-32 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${getPlaceholderUrl(selectedPlace.properties.type)})`,
                    }}
                  />
                  {/* Content */}
                  <div className="bg-white p-6 relative">
                    <button
                      onClick={() => setSelectedPlace(null)}
                      className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" stroke="#333" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.forest }}>
                      {PLACE_TYPE_META[selectedPlace.properties.type].label}
                    </span>
                    <h3 className="text-2xl font-bold mt-1 mb-2 pr-8" style={{ color: colors.dark }}>
                      {selectedPlace.properties.name}
                    </h3>
                    {selectedPlace.properties.description && (
                      <p className="text-gray-600 leading-relaxed">{selectedPlace.properties.description}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* =============================================
          STATS BAR
          ============================================= */}
      <section className="py-12 md:py-16" style={{ background: colors.forest }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: places.features.length, label: "Places" },
            { value: placesByType.size, label: "Categories" },
            { value: "100%", label: "Curated" },
            { value: "Free", label: "To Use" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-white/60 text-sm uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* =============================================
          ALL PLACES GRID
          ============================================= */}
      <section id="all-places" className="py-16 md:py-24" style={{ background: colors.cream }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: colors.dark }}>
              All Places
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">Every spot in this adventure, organized by category.</p>
          </div>

          {/* Category sections */}
          {Array.from(placesByType.entries()).map(([type, typePlaces]) => (
            <div key={type} className="mb-16 last:mb-0">
              <div className="flex items-center gap-4 mb-8">
                <h3 className="text-2xl md:text-3xl font-bold" style={{ color: colors.dark }}>
                  {PLACE_TYPE_META[type].labelPlural}
                </h3>
                <div className="flex-1 h-px bg-gray-200" />
                <span
                  className="text-sm font-semibold px-3 py-1 rounded-full"
                  style={{ background: colors.forest, color: "#fff" }}
                >
                  {typePlaces.length}
                </span>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {typePlaces.map((place) => (
                  <button
                    key={place.id}
                    onClick={() => handlePlaceSelect(place)}
                    className="group text-left rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Image */}
                    <div
                      className="h-48 relative bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${getPlaceholderUrl(place.properties.type)})`,
                      }}
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span
                          className="px-4 py-2 rounded-full bg-white text-sm font-semibold"
                          style={{ color: colors.dark }}
                        >
                          View →
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h4
                        className="text-xl font-bold mb-2 group-hover:underline underline-offset-4"
                        style={{ color: colors.dark }}
                      >
                        {place.properties.name}
                      </h4>
                      {place.properties.description && (
                        <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                          {place.properties.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* =============================================
          CTA
          ============================================= */}
      <section
        className="relative py-24 md:py-32 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${photoUrls.landscape})` }}
      >
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">Create Your Own Adventure</h2>
          <p className="text-xl text-white/80 max-w-xl mx-auto mb-10">
            Share your favorite places with the world. It&apos;s free and takes just a minute.
          </p>
          <a
            href={routes.to.home()}
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-bold transition-all hover:scale-105"
            style={{ background: colors.amber, color: colors.dark }}
          >
            Get Started Free
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* =============================================
          FOOTER
          ============================================= */}
      <footer className="py-10 px-6 md:px-10" style={{ background: colors.dark }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: colors.forest }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <a href={routes.to.home()} className="text-white font-semibold hover:opacity-80 transition-opacity">
              noma.to
            </a>
          </div>
          <div className="flex items-center gap-8 text-sm text-white/50">
            <span>Curated with care by {mapData.curatedBy?.displayName ?? "your local adventurers"}</span>
            <button onClick={handleShare} className="hover:text-white transition-colors">
              Share this map
            </button>
            <a href={routes.to.home()} className="hover:text-white transition-colors">
              Create your own
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
